import type { RecordView } from '@kit/sync/types/record-view';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { usePersonName } from '@/entities/session/hooks/use-person-name';
import { PRIORITY_OPTIONS } from '@/entities/shared/priority/priority-options';
import { RecordMeta } from '@/entities/shared/record/record-meta';
import { completeTask } from '@/entities/task/api/complete-task';
import { createTask } from '@/entities/task/api/create-task';
import { deleteTask } from '@/entities/task/api/delete-task';
import { patchTask } from '@/entities/task/api/patch-task';
import { saveTask } from '@/entities/task/api/save-task';
import { REPEAT_UNIT_LABELS } from '@/entities/task/config/repeat-unit-labels';
import { TASK_LABELS } from '@/entities/task/config/task-labels';
import { isOpenTask } from '@/entities/task/helpers/is-open-task';
import type { ChecklistStep } from '@/entities/task/schemas/checklist-step';
import { repeatUnitSchema } from '@/entities/task/schemas/repeat-unit';
import type { Task } from '@/entities/task/schemas/task';
import { type TaskKind, taskKindSchema } from '@/entities/task/schemas/task-kind';
import { taskStatusSchema } from '@/entities/task/schemas/task-status';
import { EditorSheet } from '@/shared/components/editor-sheet/editor-sheet';
import { DateField } from '@/shared/components/form/date-field';
import { FormGrid } from '@/shared/components/form/form-grid';
import { NumberField } from '@/shared/components/form/number-field';
import { numberToInput } from '@/shared/components/form/number-to-input';
import { parseNumber } from '@/shared/components/form/parse-number';
import { SelectField } from '@/shared/components/form/select-field';
import { TextField } from '@/shared/components/form/text-field';
import { TextareaField } from '@/shared/components/form/textarea-field';
import { useForm } from '@/shared/hooks/use-form';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatDate } from '@/shared/lib/dates/format-date';
import { Button } from '@/shared/ui/button/button';
import { ChecklistField } from './checklist-field';

type TaskEditorProps = {
  readonly view: RecordView<Task> | undefined;
  readonly initialKind?: TaskKind;
  readonly onClose: () => void;
};

const KIND_OPTIONS = taskKindSchema.options.map((value) => ({ value, label: TASK_LABELS.kind[value] }));
const UNIT_OPTIONS = repeatUnitSchema.options.map((value) => ({
  value,
  label: REPEAT_UNIT_LABELS[value].unit,
}));
const STATUS_OPTIONS = taskStatusSchema.options.map((value) => ({ value, label: TASK_LABELS.status[value] }));

/** The steps with text; none means no checklist (the field is absent, not empty). */
function filledSteps(checklist: readonly ChecklistStep[]): ChecklistStep[] | undefined {
  const steps = checklist.filter((step) => step.text.trim());
  return steps.length > 0 ? steps : undefined;
}

export function TaskEditor({ view, initialKind, onClose }: TaskEditorProps) {
  const { t } = useTranslation();
  const nameOf = usePersonName();
  const task = view?.data;
  const form = useForm({
    title: task?.title ?? '',
    kind: task?.kind ?? initialKind ?? 'action',
    status: task?.status ?? 'todo',
    priority: task?.priority ?? 'medium',
    starts_on: task?.starts_on ?? '',
    due_on: task?.due_on ?? '',
    remind_days: numberToInput(task?.remind_days),
    repeat_every: numberToInput(task?.repeat_every),
    repeat_unit: task?.repeat_unit ?? 'months',
    checklist: task?.checklist ?? [],
    notes: task?.notes ?? '',
  });

  async function save() {
    const saved = await form.submit(async function write() {
      const data: Partial<Task> = {
        ...task,
        ...form.values,
        repeat_every: parseNumber(form.values.repeat_every),
        remind_days: parseNumber(form.values.remind_days),
        // Months is the default, so it is only stored when it says something.
        repeat_unit: form.values.repeat_unit === 'months' ? undefined : form.values.repeat_unit,
        checklist: filledSteps(form.values.checklist),
      };
      if (view) await saveTask(view.id, data);
      else await createTask(data);
    });
    if (!saved) return;
    toast.success(view ? t('Task saved') : t('Task added'));
    onClose();
  }

  function toggleStep(checklist: ChecklistStep[]) {
    form.set('checklist', checklist);
    // A tick is saved at once, like the done button; text edits wait for Save.
    if (view) void patchTask(view.id, { checklist: filledSteps(checklist) ?? null });
  }

  async function complete() {
    if (!view) return;
    const patch = await completeTask(view);
    toast.success(
      patch.status === 'todo'
        ? t('Done. Next check: {date}', { date: formatDate(patch.due_on) })
        : t('Task done'),
    );
    onClose();
  }

  async function remove() {
    if (!view) return;
    await deleteTask(view.id);
    onClose();
  }

  return (
    <EditorSheet
      title={view ? view.data.title : t('New task')}
      saving={form.saving}
      onClose={onClose}
      onSubmit={save}
      onDelete={view ? remove : undefined}
      footerExtra={
        view && isOpenTask(view.data) ? (
          <Button type="button" variant="ghost" size="lg" onClick={() => void complete()}>
            <Check /> {t('Mark as done')}
          </Button>
        ) : undefined
      }
    >
      <TextField
        label={t('Title')}
        required
        autoFocus={!view}
        placeholder={t('e.g. Check the first aid kit')}
        {...form.field('title')}
      />
      <FormGrid>
        <SelectField label={t('Type')} options={KIND_OPTIONS} {...form.field('kind')} />
        <SelectField label={t('Priority')} options={PRIORITY_OPTIONS} {...form.field('priority')} />
      </FormGrid>
      <FormGrid>
        <DateField
          label={t('Can start on')}
          hint={t('Until then it waits, without alerts')}
          {...form.field('starts_on')}
        />
        <DateField label={t('Due on')} {...form.field('due_on')} />
      </FormGrid>
      <NumberField
        label={t('Remind days before')}
        integer
        placeholder="0"
        hint={t('Empty or 0: only on the day it is due')}
        {...form.field('remind_days')}
      />
      <FormGrid className="grid-cols-2">
        <NumberField
          label={t('Repeat every')}
          integer
          placeholder={t('Never')}
          hint={t('Marking it done schedules the next one')}
          {...form.field('repeat_every')}
        />
        <SelectField label={t('Period')} options={UNIT_OPTIONS} {...form.field('repeat_unit')} />
      </FormGrid>
      <ChecklistField {...form.field('checklist')} onToggle={toggleStep} />
      {view && <SelectField label={t('Status')} options={STATUS_OPTIONS} {...form.field('status')} />}
      <TextareaField
        label={t('Notes')}
        rows={5}
        placeholder={t('Steps, links, why…')}
        {...form.field('notes')}
      />
      {view?.data.done_on && (
        <p className="text-sm text-muted-foreground">
          {t('Last done: {date}', { date: formatDate(view.data.done_on) })}
        </p>
      )}
      {view && <RecordMeta view={view} nameOf={nameOf} />}
    </EditorSheet>
  );
}
